import { useRef, useEffect } from 'react';
import triangle from '../../images/detail/triangle.png';

const ColorBar = ({ colors, colorsName, selectedColorName }: { colors: string[], colorsName: string[], selectedColorName: string }) => {
  const canvasRef: any = useRef(null); // 创建 ref 引用 Canvas 元素
  const parentRef: any = useRef(null); // 创建 ref 引用父元素

  useEffect(() => {
    const canvas = canvasRef.current;
    const parentElement = parentRef.current;
    if (!canvas || !parentElement) return;
    const ctx = canvas.getContext('2d'); // 获取 2D 绘图上下文

    // 定义颜色条参数
    const barHeight = 10; // 颜色条高度
    const padding = 10; // 边距
    const fontSize = 12; // 文本字体大小

    // 调整画布大小和绘制颜色条的函数
    const adjustCanvasSizeAndDraw = () => {
      const parentWidth = parseFloat(window.getComputedStyle(parentElement).width);
      canvas.width = parentWidth;

      // 计算每个颜色块的宽度
      const colorCount = colors.length;
      const availableWidth = canvas.width - 2 * padding;
      const colorWidth = availableWidth / colorCount;

      // 调整画布高度以容纳文本
      canvas.height = barHeight + padding + fontSize + 20;

      // 清空画布（可选）
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (selectedColorName.endsWith('舌')) {
        // 渐变比例
        const positions = [0.11, 0.32, 0.50, 0.69, 0.89];
        // 创建渐变
        const gradient = ctx.createLinearGradient(padding, 0, canvas.width - padding, 0);
        colors.forEach((color, index) => {
          gradient.addColorStop(positions[index], color);
        });

        // 绘制渐变矩形
        const barY = padding + 12;
        const barWidth = canvas.width - 2 * padding;
        const barRadius = barHeight / 2;

        ctx.beginPath();
        ctx.moveTo(padding + barRadius, barY);
        ctx.lineTo(canvas.width - padding - barRadius, barY);
        ctx.quadraticCurveTo(canvas.width - padding, barY, canvas.width - padding, barY + barRadius);
        ctx.lineTo(canvas.width - padding, barY + barHeight - barRadius);
        ctx.quadraticCurveTo(canvas.width - padding, barY + barHeight, canvas.width - padding - barRadius, barY + barHeight);
        ctx.lineTo(padding + barRadius, barY + barHeight);
        ctx.quadraticCurveTo(padding, barY + barHeight, padding, barY + barHeight - barRadius);
        ctx.lineTo(padding, barY + barRadius);
        ctx.quadraticCurveTo(padding, barY, padding + barRadius, barY);
        ctx.closePath();

        ctx.fillStyle = gradient;
        ctx.fill();

        // 查找选中项并绘制圆形标记
        const selectedIndex = colorsName.indexOf(removeLastCharacter(selectedColorName));
        // 圆形标记绘制
        if (selectedIndex !== -1) {
          const segmentWidth = barWidth / colors.length;
          const circleX = padding + segmentWidth * selectedIndex + segmentWidth / 2;
          const circleY = barY + barHeight / 2;
          const circleRadius = 8;

          // 获取当前位置的颜色
          const getColorAtPosition = (pos: number) => {
            if (pos <= positions[0]) return colors[0];
            if (pos >= positions[positions.length - 1]) return colors[colors.length - 1];

            for (let i = 0; i < positions.length - 1; i++) {
              if (pos >= positions[i] && pos <= positions[i + 1]) {
                const ratio = (pos - positions[i]) / (positions[i + 1] - positions[i]);
                // 简化处理，直接返回较接近的颜色
                return ratio < 0.5 ? colors[i] : colors[i + 1];
              }
            }
            return colors[0];
          };

          // 计算当前位置在渐变中的比例
          const currentPosition = (circleX - padding) / barWidth;
          const circleColor = getColorAtPosition(currentPosition);

          // 绘制圆形
          ctx.beginPath();
          ctx.arc(circleX, circleY, circleRadius, 0, Math.PI * 2);
          ctx.fillStyle = circleColor;
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.stroke();

          // 绘制名称
          ctx.fillStyle = circleColor;
          ctx.font = '15px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(selectedColorName, circleX, barY - 6);
        }
      } else {
        // 循环绘制每个颜色块
        colors.forEach((color, index) => {
          const gap = 2; // 间隙大小
          const x = padding + index * (colorWidth + gap); // 重新计算x坐标
          const y = padding;
          const height = barHeight;
          const borderRadius = 6; // 圆角半径

          // 绘制带圆角的矩形
          ctx.beginPath();

          // 根据索引设置圆角
          let tl = 0, tr = 0, br = 0, bl = 0;

          if (index === 0) {
            tl = borderRadius;
            bl = borderRadius;
          } else if (index === colors.length - 1) {
            tr = borderRadius;
            br = borderRadius;
          }

          // 圆角矩形绘制
          ctx.moveTo(x + tl, y);
          ctx.lineTo(x + colorWidth - tr, y);
          ctx.quadraticCurveTo(x + colorWidth, y, x + colorWidth, y + tr);
          ctx.lineTo(x + colorWidth, y + height - br);
          ctx.quadraticCurveTo(x + colorWidth, y + height, x + colorWidth - br, y + height);
          ctx.lineTo(x + bl, y + height);
          ctx.quadraticCurveTo(x, y + height, x, y + height - bl);
          ctx.lineTo(x, y + tl);
          ctx.quadraticCurveTo(x, y, x + tl, y);

          ctx.closePath();
          // 设置填充颜色
          ctx.fillStyle = color;

          // 绘制
          ctx.fill();

          // 绘制对应的颜色名称
          ctx.fillStyle = index === 0 ? '#666666' : colors[index]; // 设置文本颜色为黑色
          ctx.font = `${fontSize}px Arial`; // 设置字体样式
          ctx.textAlign = 'center'; // 文本居中对齐
          ctx.fillText(colorsName[index], x + colorWidth / 2, padding + barHeight + fontSize + 5); // 绘制文本

          // 如果当前颜色名称与 selectedColorName 匹配，则绘制倒三角
          if (colorsName[index].includes(removeLastCharacter(selectedColorName))) {
            const triangleWidth = 20;
            const triangleHeight = 20;
            const triangleX = x + colorWidth / 2 - triangleWidth / 2;
            const triangleY = padding - 15;

            // 创建图片对象并绘制
            const img = new Image();
            img.onload = () => {
              ctx.drawImage(img, triangleX, triangleY, triangleWidth, triangleHeight);
            };
            img.src = triangle;
          }
        });
      }
    };

    // 初始化时绘制一次
    adjustCanvasSizeAndDraw();

    // 使用 ResizeObserver 监听父元素宽度变化
    const resizeObserver = new ResizeObserver(() => {
      adjustCanvasSizeAndDraw();
    });

    resizeObserver.observe(parentElement);

    // 清理函数
    return () => {
      resizeObserver.unobserve(parentElement);
    };
  }, [selectedColorName, colors, colorsName]); // 将 colors 和 colorsName 加入依赖数组

  function removeLastCharacter(text: string) {
    if (text.length === 0) return ''; // 空字符串处理
    return text.slice(0, -1).trim(); // 截取除最后一个字符外的所有内容，并去空格
  }

  return (
    <div ref={parentRef} style={{ width: '100%', marginBottom: '10px' }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

export default ColorBar;